import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useSettings } from "../../lib/useSettings";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Loader2, Lock, Wrench, Wind, Droplets, Cog, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const iconMap = { Wind, Droplets, Wrench, Cog };

export default function LoginPage() {
  const { login } = useAuth();
  const { settings: s } = useSettings();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenue !");
      nav("/admin/dashboard");
    } catch { toast.error("Identifiants invalides"); }
    finally { setLoading(false); }
  };

  const companyName = s?.company_name || "KLOUB'AIR";
  const m = companyName.match(/^(.*?)(AIR)(.*)$/i);

  return (
    <div className="admin-scope min-h-screen grid md:grid-cols-2 bg-white">
      {/* Back to site — floating top-left */}
      <Link
        to="/"
        className="absolute top-4 left-4 md:top-6 md:left-6 z-20 inline-flex items-center gap-2 px-3.5 h-9 rounded-full bg-white/90 backdrop-blur border border-[hsl(var(--border))] text-[color:var(--brand-navy)] hover:bg-white hover:border-[color:var(--brand-navy)] hover:shadow-md transition-all text-xs font-semibold"
        data-testid="login-back-home"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Retour au site
      </Link>

      {/* LEFT — form */}
      <div className="p-8 md:p-14 flex flex-col justify-center">
        <div className="max-w-md w-full mx-auto">
          {s?.logo_url ? (
            <div className="mb-8 flex items-center gap-3">
              <img src={s.logo_url} alt={companyName} className="h-16 md:h-20 w-auto drop-shadow-md" />
              <div className="leading-tight">
                <div className="font-display font-extrabold text-[color:var(--brand-navy)] text-lg md:text-xl">
                  {m ? <>{m[1]}<span className="text-[color:var(--brand-sky)]">{m[2]}</span>{m[3]}</> : companyName}
                </div>
                <div className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-[0.2em]">Back-office</div>
              </div>
            </div>
          ) : (
            <div className="w-12 h-12 rounded-2xl bg-[color:var(--brand-navy)] text-white flex items-center justify-center mb-6"><Lock className="w-5 h-5" /></div>
          )}
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[color:var(--brand-navy)] mb-2">Bon retour parmi nous</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-8">Connectez-vous pour gérer votre site.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="mt-1.5 h-11" data-testid="login-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Mot de passe</Label>
              <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className="mt-1.5 h-11" data-testid="login-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy)]/90" data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
            </Button>
          </form>
        </div>
      </div>

      {/* RIGHT — showcase services */}
      <div className="hidden md:flex flex-col justify-center relative bg-[color:var(--brand-navy)] overflow-hidden p-14">
        <img src="/uploads/cms/hero/kap-split-facade-provence.webp" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-br from-[color:var(--brand-navy)] via-[color:var(--brand-navy)]/95 to-[#0f223c]" />
        <div className="relative max-w-lg">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--brand-sky)] font-bold mb-3">
            {companyName} · {s?.sector || "Climatisation & Plomberie"}
          </div>
          <h2 className="font-display text-3xl lg:text-4xl font-black leading-tight text-white">
            Nos prestations au service de votre confort.
          </h2>
          <p className="mt-3 text-white/70 text-sm max-w-md">
            {s?.tagline || "Installation, dépannage et entretien dans un rayon de 40 km autour de Grans."}
          </p>

          <ul className="mt-8 space-y-2" data-testid="login-services-list">
            {services.slice(0, 6).map((sv) => {
              const Icon = iconMap[sv.icon] || Wrench;
              return (
                <li key={sv.id} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
                  <div className="w-9 h-9 rounded-lg bg-[color:var(--brand-sky)]/15 text-[color:var(--brand-sky)] flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-white text-sm">{sv.title}</div>
                    <div className="text-white/60 text-[11px] leading-relaxed line-clamp-2">{sv.short_description}</div>
                  </div>
                </li>
              );
            })}
            {!services.length && (
              <li className="text-white/50 text-sm">Chargement des services…</li>
            )}
          </ul>

          <div className="mt-8 flex items-center gap-4 text-white/70 text-xs">
            <div>
              <div className="font-display text-2xl font-black text-white">{services.length}</div>
              <div className="uppercase tracking-wider text-[10px]">Prestations</div>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div>
              <div className="font-display text-2xl font-black text-white">40 km</div>
              <div className="uppercase tracking-wider text-[10px]">Rayon d'action</div>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div>
              <div className="font-display text-2xl font-black text-white">Pro</div>
              <div className="uppercase tracking-wider text-[10px]">Entretien & Dépannage</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
